import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box } from '@mui/material';

import { CHART_AXIS_PROPS } from 'constants/chart';
import { formatDate } from 'helper/DateLib';
import { formatPillarTrackerData } from 'helper/formatPillarTrackerData';
import { MultiSeriesTooltip } from 'components/charts/MultiSeriesTooltip';
import { NoData } from 'components/simple';

import { LineChartCustomLabel } from './LineChartCustomLabel';

const sevenRings = {
  ticks: [1, 2, 3, 4, 5, 6, 7],
  domain: [0, 7],
};

const TOOLTIP_WRAPPER_STYLE = { zIndex: 1 };

const styles = {
  noData: { top: '35%' },
};

const defaultTickFormatter = (unixTime) => formatDate(unixTime, 'DD/MM');

export const LineChartComponent = ({
  data = [{ pillars: [] }],
  keyEvent = null,
  loading = false,
  dateTicks = [],
  tickFormatter = defaultTickFormatter,
}) => {
  const { chartData, lines, pointsWithPillarsCount } = useMemo(() => formatPillarTrackerData(data), [data]);

  const ticks = useMemo(
    () => uniq([...dateTicks, ...(keyEvent ? [keyEvent.date] : [])].toSorted((a, b) => a - b)),
    [dateTicks, keyEvent],
  );

  return (
    <Box width="100%" height={300}>
      {!pointsWithPillarsCount && !loading && <NoData text="No data available for analysis" sx={styles.noData} />}

      <ResponsiveContainer minWidth={600} width="99%" height="100%">
        <LineChart
          width={500}
          height={300}
          data={loading ? [] : chartData}
          margin={{
            top: 25,
            right: 0,
            left: 0,
            bottom: 0,
          }}
        >
          <XAxis
            {...CHART_AXIS_PROPS}
            type="number"
            dataKey="name"
            scale="time"
            domain={[ticks[0], ticks[ticks.length - 1]]}
            ticks={ticks}
            padding={{ left: 45, right: 10 }}
            tickFormatter={tickFormatter}
          />
          <YAxis {...CHART_AXIS_PROPS} domain={sevenRings.domain} ticks={sevenRings.ticks} />
          <Tooltip wrapperStyle={TOOLTIP_WRAPPER_STYLE} content={<MultiSeriesTooltip />} />
          {lines.map((line) => (
            <Line
              {...line}
              connectNulls
              key={line.name}
              type="monotone"
              strokeWidth={3}
              dot={pointsWithPillarsCount === 1}
            />
          ))}
          <ReferenceLine
            type="number"
            x={keyEvent?.date}
            stroke="#4a5357"
            strokeWidth={2}
            strokeDasharray="10"
            label={(props) => <LineChartCustomLabel {...props} keyEvent={keyEvent} />}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

LineChartComponent.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      pillars: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ),
  keyEvent: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.number,
  }),
  dateTicks: PropTypes.arrayOf(PropTypes.number),
  loading: PropTypes.bool,
  tickFormatter: PropTypes.func,
};
